import {
	defer,
	EMPTY,
	from,
	Observable,
	race,
	SchedulerLike,
	timer,
} from 'rxjs';
import { catchError, exhaustMap, filter, map, take } from 'rxjs/operators';

import {
	getPaymentMethodSetupStatusApi,
	PaymentMethodSetupStatusEnum,
} from '@mopla/data-models';

export type TPayByBankSetupResult = 'SUCCEEDED' | 'FAILED';

/**
 * Fetches the Pay by Bank setup status once (GET /api/passengers/paymentMethodSetupStatus).
 * - NONE: no Pay by Bank setup is open. Right after PENDING this means the setup is finished
 *   (the backend has set the generated SEPA payment method as default) — it is not a success flag by itself;
 *   it is also returned when the passenger deliberately switched to another payment method.
 * - PENDING: the return report was received; waiting for Stripe to generate the SEPA payment method (max 60 s).
 * - FAILED: the setup did not complete in time or the generated payment method was rejected (e.g. fraud check).
 */
export const fetchPaymentMethodSetupStatus =
	async (): Promise<PaymentMethodSetupStatusEnum> => {
		const { data } = await getPaymentMethodSetupStatusApi();
		return data.status;
	};

export const PAY_BY_BANK_COUNTDOWN_SECONDS = 60;
export const PAY_BY_BANK_POLL_INTERVAL_MS = 3000;
export const PAY_BY_BANK_GRACE_MS = 10000;

interface IOptions {
	intervalMs?: number;
	deadlineMs?: number;
	scheduler?: SchedulerLike;
}

/**
 * Polls the setup status until it is terminal and emits exactly one result.
 * Backend contract: PENDING is set before Stripe's webhook arrives, so NONE
 * after PENDING means success; FAILED means the setup did not complete in time or was rejected.
 * Rejected requests are ignored for that tick; the deadline yields FAILED.
 */
export const createPayByBankSetupPolling$ = (
	fetchStatus: () => Promise<PaymentMethodSetupStatusEnum>,
	{
		intervalMs = PAY_BY_BANK_POLL_INTERVAL_MS,
		deadlineMs = PAY_BY_BANK_COUNTDOWN_SECONDS * 1000 + PAY_BY_BANK_GRACE_MS,
		scheduler,
	}: IOptions = {}
): Observable<TPayByBankSetupResult> => {
	const poll$ = timer(0, intervalMs, scheduler).pipe(
		exhaustMap(() =>
			defer(() => from(fetchStatus())).pipe(catchError(() => EMPTY))
		),
		filter((status) => status !== PaymentMethodSetupStatusEnum.PENDING),
		map(
			(status): TPayByBankSetupResult =>
				status === PaymentMethodSetupStatusEnum.NONE ? 'SUCCEEDED' : 'FAILED'
		),
		take(1)
	);

	const deadline$ = timer(deadlineMs, scheduler).pipe(
		map((): TPayByBankSetupResult => 'FAILED')
	);

	return race(poll$, deadline$);
};
